import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Grid,
  InputAdornment,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Popover,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckIcon from '@mui/icons-material/Check';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonRemoveOutlined from '@mui/icons-material/PersonRemoveOutlined';
import { Reply } from 'lucide-react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import {
  TASK_MODAL_WIDTH_L,
  TASK_MODAL_WIDTH_M,
  TASK_MODAL_WIDTH_S,
  TASK_MODAL_WIDTH_SX,
} from 'app/components/Tasks/CreateTaskNew/CreateTaskModalNew.styles';
import { GenericDialogWithNavigation } from 'app/components/Alerts/GenericDialogWithNavigation';
import { Cancel } from 'app/components/Common/CancelModal/Cancel';
import { getAttachmentIcon, getFileName } from 'app/components/Notification/Common/AttachmentFileTypes';
import { ReplyToTaskModalNew } from 'app/components/Tasks/CreateTaskNew';
import { IInternalTaskModalDetails, IUser, SubTypeId } from 'app/models/Tasks/Tasks';
import {
  ITaskChainAttachment,
  ITaskChainBrandRef,
  ITaskChainItem,
  ITaskChainRecipient,
} from 'app/models/Tasks/TaskChain';
import { SearchType } from 'app/models/GlobalSearch/GlobalSearch';
import { ICoverageDetails } from 'app/models/UserCoverage/UserCoverage';
import { missingField } from 'app/constants';
import { httpSuccess } from 'app/services/serviceHelpers';
import { getAMIECategorySearch } from 'app/services/SharedServices/SharedServices';
import { updateTaskChainStatus } from 'app/services/TaskServices/TaskServices';
import { selectUser } from 'oidc/user.selectors';
import { coverageActions } from 'store/redux-store/user-coverage/async-actions';
import { useActiveCoverageForTaskActions } from 'store/redux-store/user-coverage/useActiveCoverageForTaskActions';
import { taskDetailsActions } from '../../../Tasks/store/Tasks.redux';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import {
  selectIsTaskChainLoading,
  selectIsTaskChainSaveLoading,
  selectIsTaskChainStatusLoading,
  selectTaskChainDeleteLoading,
  selectTaskChainDeleteSucceeded,
  selectTaskChainDetailsData,
  selectTaskChainError,
  selectTaskChainLoopOutLoading,
  selectTaskChainLoopedOut,
  selectTaskChainStatusUpdated,
  selectTaskChainUpdatedId,
} from '../../../Tasks/store/Tasks.selectors';
import {
  getLoopOutRecipientUnified,
  getOnBehalfRecipientRowsUnified,
  isUserCoveringTaskSender,
  userCanEditViaClientCoverage,
} from './viewEditTaskCoverage';
import { buildNotificationOpenReadStatusRequests } from './notificationTaskChainReadStatus';

type ITaskFormState = {
  taskId: number;
  subject: string;
  text: string;
  followupDate: string;
  followupDateEditable: boolean;
  priorityId: number;
  optionApproval: boolean;
  flag: boolean;
  selectedRecipientId: number;
  selectedStatusId: number;
};

type ITaskFormStateMap = Record<number, ITaskFormState>;

type ISidebarRow = {
  label: string;
  value?: React.ReactNode;
  secondary?: React.ReactNode;
};

/** API status ids */
const STATUS_NEW = 1;
const STATUS_PENDING = 2;
const STATUS_COMPLETED = 3;
/** UI-only; save maps to Completed (3) for every task in the chain */
const STATUS_COMPLETED_ENTIRE_BRANCH = 101;
/** UI-only; on-behalf-of only. Save maps to Completed (3) for the active task and suppresses the SP cascade to the covered counterpart row. */
const STATUS_COMPLETED_ONLY_MY_TASK = 102;

/** Matches `onSave` logic for a single task — used so `canSave` reflects what Save will actually persist (active card only). */
const computeTaskSaveWork = (
  task: ITaskChainItem,
  formState: ITaskFormState,
  initial: ITaskFormState,
  flatTasks: ITaskChainItem[],
): { shouldUpdateStatus: boolean; bodyDirty: boolean } => {
  const rid = formState.selectedRecipientId;
  const sid = formState.selectedStatusId;

  const hasBranchStatusWork =
    sid === STATUS_COMPLETED_ENTIRE_BRANCH &&
    flatTasks.some(t => {
      const rec = t.recipients?.find(r => r.recipientId === rid);
      return rec && rec.statusId !== STATUS_COMPLETED;
    });

  const currentRecipient = task.recipients?.find(r => r.recipientId === rid);
  // STATUS_COMPLETED_ONLY_MY_TASK is a single-task status whose effective DB status is Completed (3).
  const effectiveSingleStatus = sid === STATUS_COMPLETED_ONLY_MY_TASK ? STATUS_COMPLETED : sid;
  const hasSingleStatusWork =
    sid !== STATUS_COMPLETED_ENTIRE_BRANCH &&
    !!(currentRecipient && currentRecipient.statusId !== effectiveSingleStatus);

  const statusSelectionDirty =
    initial.selectedRecipientId !== formState.selectedRecipientId ||
    initial.selectedStatusId !== formState.selectedStatusId;

  const shouldUpdateStatus =
    statusSelectionDirty && (hasBranchStatusWork || hasSingleStatusWork);

  const bodyDirty =
    initial.text !== formState.text ||
    (formState.followupDateEditable && initial.followupDate !== formState.followupDate) ||
    initial.priorityId !== formState.priorityId ||
    initial.optionApproval !== formState.optionApproval ||
    initial.flag !== formState.flag ||
    initial.subject !== formState.subject;

  return { shouldUpdateStatus, bodyDirty };
};

const toTaskChainDueDate = (formState: ITaskFormState): string | null =>
  formState.followupDate ? new Date(formState.followupDate).toISOString() : null;

const statusOptions = [
  { label: 'New', value: STATUS_NEW },
  { label: 'Pending', value: STATUS_PENDING },
  { label: 'Completed', value: STATUS_COMPLETED },
];

const extendedStatusOptions = [
  ...statusOptions,
  { label: 'Completed (Entire Branch)', value: STATUS_COMPLETED_ENTIRE_BRANCH },
];

/**
 * On-behalf-of dropdown options. Order is intentional and matches the design:
 * Completed first, followed by New / Pending, then the two scoped Completed variants.
 * "Completed (Only My Task)" is the only option that suppresses the SP cascade to the covered counterpart.
 */
const onBehalfStatusOptions = [
  { label: 'Completed', value: STATUS_COMPLETED },
  { label: 'New', value: STATUS_NEW },
  { label: 'Pending', value: STATUS_PENDING },
  { label: 'Completed (Only My Task)', value: STATUS_COMPLETED_ONLY_MY_TASK },
  { label: 'Completed (Entire Branch)', value: STATUS_COMPLETED_ENTIRE_BRANCH },
];

const priorityOptions = [
  { label: 'High', value: 3 },
  { label: 'Normal', value: 2 },
  { label: 'Low', value: 1 },
];

const DESCRIPTION_PREVIEW_LINES = 6;
const ENABLE_REPLY_ACTION = true;
const ENABLE_ATTACHMENT_DELETE = true;

const textLabels = {
  attachments: 'Attachments',
  status: 'Status',
  priority: 'Priority',
  followUpDate: 'Follow-Up Date',
  subject: 'Subject',
  viewMore: 'View More',
  viewLess: 'View Less',
  from: 'From:',
  to: 'To:',
  cc: 'CC:',
  deletedAttachments: 'Deleted attachments:',
  onBehalfOf: 'On behalf of',
  setStatus: 'Set status',
  noRecipientStatusData: 'No recipient status data',
  deleteAttachmentTitle: 'Delete Attachment?',
  deleteAttachmentMessage: 'Are you sure you want to delete the attachment?',
  cancel: 'Cancel',
  confirm: 'Yes',
};

const headerControlFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    borderRadius: '4px',
    minHeight: '40px',
    fontSize: '13px',
    '& fieldset': {
      borderColor: '#D3D3D3',
    },
    '&:hover fieldset': {
      borderColor: '#BFBFBF',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1593D1',
    },
    '&.Mui-disabled': {
      backgroundColor: '#F8F8F8',
    },
  },
  '& .MuiOutlinedInput-input': {
    py: '9px',
    px: '12px',
  },
  '& .MuiSelect-select': {
    py: '9px',
    px: '12px',
    display: 'flex',
    alignItems: 'center',
  },
};

const embeddedHeaderLabelSx = {
  ...headerControlFieldSx,
  '& .MuiInputLabel-root': {
    color: '#8A8A8A',
    fontSize: '12px',
    lineHeight: '16px',
  },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    transform: 'translate(14px, -7px) scale(0.75)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#8A8A8A',
  },
};

const followUpDateFieldSx = {
  ...headerControlFieldSx,
  '& .MuiOutlinedInput-root': {
    ...headerControlFieldSx['& .MuiOutlinedInput-root'],
    pr: 0.5,
  },
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    opacity: 0,
    position: 'absolute',
    right: 0,
    width: '100%',
    height: '100%',
    cursor: 'pointer',
  },
};

const subjectHeadlineFieldSx = {
  '& .MuiInputBase-root': {
    backgroundColor: 'transparent',
    borderBottom: 'none',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '28px',
    /** Match `taskChainFieldLabelTypographySx` (Status / From / To / CC labels) */
    color: '#555555',
    pb: 0.5,
    '&.Mui-disabled': {
      color: '#555555',
      WebkitTextFillColor: '#555555',
      opacity: 1,
    },
  },
  '& .MuiInputBase-input': {
    px: 0,
    py: 0.25,
  },
};

/** Always-on row dividers; `pb` is space between content above and the divider line (Figma) */
const taskChainSectionDividerRowSx = {
  borderBottom: '1px solid',
  borderColor: 'divider',
  pb: 1.5,
};

const taskChainFieldLabelTypographySx = {
  width: 44,
  fontSize: '13px',
  fontWeight: 600,
  color: '#555555',
};

const viewEditTaskModalStyles = makeStyles<{ expanded: boolean }>()((theme, { expanded }) => ({
  modalContainer: {
    '& .MuiDialog-paper': {
      ...(expanded
        ? {
            maxWidth: 'none',
            width: '100%',
            height: 'auto',
            maxHeight: 'none',
          }
        : {
            maxWidth: TASK_MODAL_WIDTH_SX,
            width: '100%',
            height: 'auto',
            maxHeight: 'min(800px, 90vh)',
            [theme.breakpoints.up('sm')]: {
              maxWidth: TASK_MODAL_WIDTH_S,
            },
            [theme.breakpoints.up('md')]: {
              maxWidth: TASK_MODAL_WIDTH_M,
            },
            [theme.breakpoints.up('lg')]: {
              maxWidth: TASK_MODAL_WIDTH_L,
            },
            [theme.breakpoints.up('xl')]: {
              maxWidth: TASK_MODAL_WIDTH_L,
            },
          }),
      color: '#333333',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      '& .MuiDialogContent-root': {
        padding: 0,
        flex: '1 1 auto',
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '& .dialogContentRootNoActions': {
          padding: 0,
        },
      },
      '& .MuiDialogActions-root': {
        padding: '16px 24px',
      },
    },
    '& .MuiDialog-paperFullScreen': {
      maxWidth: '100% !important',
      width: '100% !important',
      height: '100% !important',
      margin: 0,
      borderRadius: 0,
    },
  },
  link: {
    color: '#006FB9',
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const toDateInput = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const priorityIdFromApi = (priority?: string) => {
  const lower = (priority || '').toLowerCase();
  if (lower.includes('high')) return 3;
  if (lower.includes('low')) return 1;
  return 2;
};

const getEmployeeName = (employee?: { firstName?: string; lastName?: string } | null) =>
  [employee?.firstName, employee?.lastName].filter(Boolean).join(' ').trim();

const getStatusLabel = (statusId?: number) => {
  if (statusId == null) return '-';
  // Search the on-behalf list first so STATUS_COMPLETED_ONLY_MY_TASK resolves to its UI label.
  return (
    onBehalfStatusOptions.find(option => option.value === statusId)?.label ||
    extendedStatusOptions.find(option => option.value === statusId)?.label ||
    '-'
  );
};

const getStatusColor = (statusId?: number) => {
  const isCompletedLike =
    statusId === STATUS_COMPLETED ||
    statusId === STATUS_COMPLETED_ENTIRE_BRANCH ||
    statusId === STATUS_COMPLETED_ONLY_MY_TASK;
  if (isCompletedLike) {
    return '#34B233';
  }
  if (statusId === STATUS_PENDING) return '#F5A623';
  if (statusId === STATUS_NEW) return '#1E88E5';
  return '#9E9E9E';
};

/** Matches Tasks grid Status New (`NewChip` in StatusBadgeGrid) for action icon hover */
const TASK_GRID_STATUS_NEW_BLUE = '#10a3de';
const TASK_GRID_STATUS_NEW_PALE_BG = '#cee3eb';

/** White tooltip shell — aligned with task search grid tooltips */
const chainTaskActionTooltipComponentsProps = {
  tooltip: {
    sx: {
      backgroundColor: '#fff',
      color: '#333333',
      border: '1px solid rgba(224, 224, 224, 1)',
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
      fontSize: '12px',
    },
  },
  arrow: { sx: { color: '#fff' } },
};

/** Same chrome as `chainTaskActionTooltipComponentsProps` but tighter gap under the anchor (MUI default is 14px for bottom) */
const recipientChipTooltipComponentsProps = {
  tooltip: {
    sx: {
      ...chainTaskActionTooltipComponentsProps.tooltip.sx,
      margin: 0,
      /** Overrides MUI Tooltip `tooltipPlacementBottom` marginTop (14px) */
      marginTop: '6px !important',
      marginLeft: '2px',
      marginRight: '2px',
    },
  },
  arrow: chainTaskActionTooltipComponentsProps.arrow,
} as React.ComponentProps<typeof Tooltip>['componentsProps'];

const chainTaskActionIconButtonSx = {
  width: 36,
  height: 36,
  padding: 0,
  borderRadius: '50%',
  border: 'none',
  backgroundColor: 'transparent',
  color: '#333333',
  '&:hover:not(.Mui-disabled)': {
    backgroundColor: TASK_GRID_STATUS_NEW_PALE_BG,
    color: TASK_GRID_STATUS_NEW_BLUE,
  },
  '&.Mui-disabled': {
    opacity: 1,
    backgroundColor: 'transparent',
    color: '#BDBDBD',
  },
};

const getAttachmentDisplayName = (attachment?: ITaskChainAttachment | null) =>
  getFileName(attachment?.attachmentUrl || attachment?.sasAttachmentUrl || '') || '-';

const getAttachmentExtension = (attachment?: ITaskChainAttachment | null) =>
  getAttachmentDisplayName(attachment).split('.').pop()?.toLowerCase();

const formatShortDate = (value?: string | null) => {
  if (!value) return '';
  const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    return `${month}/${day}/${year}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const datePart = date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const timePart = date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${datePart} ${timePart}`;
};

const getRecipientStatusHistoryText = (recipient: ITaskChainRecipient) => {
  const status = getStatusLabel(recipient.statusId);
  const statusUpdateDateTime = formatDateTime(recipient.statusUpdateDate);
  return statusUpdateDateTime ? `${status} - ${statusUpdateDateTime}` : status;
};

const getToRecipients = (recipients?: ITaskChainRecipient[] | null) =>
  (recipients || []).filter(recipient => !recipient.isCC);

const getCcRecipients = (recipients?: ITaskChainRecipient[] | null) =>
  (recipients || []).filter(recipient => !!recipient.isCC);

const toFormState = (
  task: ITaskChainItem,
  currentEmployeeId?: number,
  activeCoverage?: ICoverageDetails[] | null,
): ITaskFormState => {
  const selfRecipient =
    currentEmployeeId != null && currentEmployeeId > 0
      ? task.recipients?.find(r => r.recipientId === currentEmployeeId)
      : undefined;
  const toRecipients = getToRecipients(task.recipients);
  const onBehalfUnified = getOnBehalfRecipientRowsUnified(task, currentEmployeeId, activeCoverage);
  const defaultRecipient =
    selfRecipient ||
    (!selfRecipient && onBehalfUnified.length > 0 ? onBehalfUnified[0] : undefined) ||
    toRecipients[0] ||
    task.recipients?.[0];

  return {
    taskId: task.taskId,
    subject: task.subject || '',
    text: task.text || '',
    followupDate: toDateInput(task.followupDate),
    followupDateEditable: task.followupDate != null,
    priorityId: priorityIdFromApi(task.priority),
    optionApproval: !!task.isOptionApproval,
    flag: !!task.isFlagged,
    selectedRecipientId: defaultRecipient?.recipientId || 0,
    selectedStatusId: defaultRecipient?.statusId || task.status?.id || STATUS_PENDING,
  };
};

/** Stable key for coverage-driven form logic; avoids resets when the saga replaces the array with an equal payload. */
const serializeActiveCoverageForForm = (activeCoverage?: ICoverageDetails[] | null): string => {
  const list = activeCoverage ?? [];
  const normalized = [...list].sort((a, b) => String(a.id).localeCompare(String(b.id)));
  return JSON.stringify(normalized);
};

const flattenTaskChain = (root?: ITaskChainItem | null): ITaskChainItem[] => {
  if (!root) return [];

  const items: ITaskChainItem[] = [];
  const visit = (node: ITaskChainItem) => {
    items.push(node);
    node.subTasks?.forEach(visit);
  };
  visit(root);

  return [...items].sort((a, b) => {
    const aTime = a.sentDate ? new Date(a.sentDate).getTime() : Number.MAX_SAFE_INTEGER;
    const bTime = b.sentDate ? new Date(b.sentDate).getTime() : Number.MAX_SAFE_INTEGER;
    if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
    return aTime - bTime;
  });
};

/** Latest row per id when the same `taskAttachmentId` is repeated with evolving fields (e.g. deleteDate). */
const latestAttachmentsByTaskAttachmentId = (
  attachments?: ITaskChainAttachment[] | null,
): ITaskChainAttachment[] => {
  const byId = new Map<number, ITaskChainAttachment>();
  for (const attachment of attachments || []) {
    byId.set(attachment.taskAttachmentId, attachment);
  }
  return Array.from(byId.values());
};

/** All attachments in the chain (root + subtasks), deduped by id; later tasks / list positions win. */
const collectAttachmentsFromTaskChain = (tasks: ITaskChainItem[]): ITaskChainAttachment[] => {
  const byId = new Map<number, ITaskChainAttachment>();
  for (const task of tasks) {
    for (const attachment of task.attachments || []) {
      byId.set(attachment.taskAttachmentId, attachment);
    }
  }
  return Array.from(byId.values());
};

const isCurrentUserTaskSender = (
  currentEmployeeId: number | undefined,
  sender: ITaskChainItem['sender'],
) =>
  currentEmployeeId != null &&
  currentEmployeeId > 0 &&
  sender?.employeeId != null &&
  sender.employeeId === currentEmployeeId;

/** True when the current user is on To or CC for this task. */
const isCurrentUserTaskRecipient = (
  currentEmployeeId: number | undefined,
  recipients: ITaskChainRecipient[],
) =>
  currentEmployeeId != null &&
  currentEmployeeId > 0 &&
  recipients.some(r => r.recipientId === currentEmployeeId);

/** True when this task is opened in coverage mode for a covered employee (recipient). */
const isCurrentUserCoveringRecipient = (task: ITaskChainItem) =>
  !!task.isCoverage && task.coveredEmployee?.id != null && task.coveredEmployee.id > 0;

/** True when the current user is covering the task sender (Cosmos) — `coveredEmployee` matches sender. */
const isCurrentUserCoveringSender = (task: ITaskChainItem) =>
  !!task.isCoverage &&
  task.coveredEmployee?.id != null &&
  task.sender?.employeeId != null &&
  task.coveredEmployee.id === task.sender.employeeId;

/** Direct To/CC recipient or coverage-based editor (same rules as per-card `canEditTaskFields`). */
const canViewerEditTaskChainTaskFields = (
  employeeId: number | undefined,
  task: ITaskChainItem,
  activeCoverageList: ICoverageDetails[] | null | undefined,
) =>
  isCurrentUserTaskRecipient(employeeId, task.recipients || []) ||
  isCurrentUserCoveringRecipient(task) ||
  userCanEditViaClientCoverage(task, employeeId, activeCoverageList);

const getRecipientChipLabel = (r: ITaskChainRecipient) => `${r.firstName} ${r.lastName}`.trim();

const normalizePlacementBrand = (value?: ITaskChainItem['brandPlacement']) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();

  const ref = value as ITaskChainBrandRef;
  return (ref.brandName || ref.name || '').trim();
};

/** City, ST 99999 — omits missing segments. */
const formatFacilityAddressLine = (
  city?: string | null,
  state?: string | null,
  postalCode?: string | null,
): string | undefined => {
  const c = city?.trim();
  const s = state?.trim();
  const z = postalCode?.trim();
  if (!c && !s && !z) return undefined;

  const cityState = c && s ? `${c}, ${s}` : c || s || '';
  return z ? (cityState ? `${cityState} ${z}` : z) : cityState || undefined;
};

const isDeletedAttachment = (attachment?: ITaskChainAttachment | null) => !!attachment?.deleteDate;

const getDeletedAttachmentsForTask = (
  task: ITaskChainItem,
  locallyDeleted: ITaskChainAttachment[],
): ITaskChainAttachment[] => {
  const deletedById = new Map<number, ITaskChainAttachment>();
  latestAttachmentsByTaskAttachmentId(task.attachments).forEach(attachment => {
    if (isDeletedAttachment(attachment)) {
      deletedById.set(attachment.taskAttachmentId, attachment);
    }
  });
  locallyDeleted.forEach(attachment => {
    if (attachment.taskId === task.taskId) {
      deletedById.set(attachment.taskAttachmentId, attachment);
    }
  });
  return Array.from(deletedById.values());
};

const sortAttachmentsAlphabetically = (attachments: ITaskChainAttachment[]): ITaskChainAttachment[] =>
  [...attachments].sort((a, b) =>
    getAttachmentDisplayName(a).localeCompare(getAttachmentDisplayName(b), undefined, {
      sensitivity: 'base',
      numeric: true,
    }),
  );

const mapPriorityToId = (priority?: string | null) => {
  const normalized = (priority || '').trim().toLowerCase();
  if (normalized === 'high') return 3;
  if (normalized === 'low') return 1;
  return 2;
};

type IReplyParentTaskDetails = IInternalTaskModalDetails & { isFollowup?: boolean };

const toReplyTaskDetails = (task: ITaskChainItem): IReplyParentTaskDetails => {
  const createdBy = task.sender
    ? {
        id: task.sender.employeeId,
        firstName: task.sender.firstName,
        lastName: task.sender.lastName,
      }
    : undefined;

  const emptySubCategory: IInternalTaskModalDetails['subCategory1'] = {
    subTypeId: SubTypeId.License,
    subTypeText: '',
    subTypeDataId: 0,
    subTypeDataText: '',
  };

  const assignedTo = (task.recipients || []).map(recipient => ({
    id: recipient.recipientId,
    firstName: recipient.firstName,
    lastName: recipient.lastName,
    statusId: recipient.statusId,
    isActive: recipient.isActive,
  })) as IUser[];

  const details: IReplyParentTaskDetails = {
    rootTaskId: task.rootTaskId,
    placementId: task.placementId,
    taskId: task.taskId,
    candidateId: task.candidate?.id,
    orderId: task.orderId,
    facilityId: task.facility?.id,
    unitId: task.unit?.id,
    assignedTo,
    createdBy,
    task: task.subject || '',
    description: task.text || '',
    taskCategory: task.taskCategoryId != null ? String(task.taskCategoryId) : task.category || '',
    dueDate: task.followupDate || undefined,
    taskPriorityId: mapPriorityToId(task.priority),
    cc: [],
    subCategory1: emptySubCategory,
    subCategory2: emptySubCategory,
    isFollowup: !!task.isFollowup,
  };

  return details;
};

const SidebarRow = ({ label, value, secondary }: ISidebarRow) => {
  if (!value && !secondary) return null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '56px minmax(0, 1fr)',
        columnGap: 1,
        alignItems: 'start',
      }}
    >
      <Typography
        sx={{
          fontSize: '13px',
          lineHeight: '20px',
          color: '#444444',
          fontWeight: 700,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="div"
          sx={{
            fontSize: '13px',
            lineHeight: '20px',
            color: '#333333',
            wordBreak: 'break-word',
          }}
        >
          {value}
        </Typography>
        {secondary && (
          <Box
            sx={{
              mt: 0.25,
              fontSize: '12px',
              lineHeight: '18px',
              color: '#666666',
              whiteSpace: 'pre-line',
            }}
          >
            {secondary}
          </Box>
        )}
      </Box>
    </Box>
  );
};

/** Shown while task chain details are loading (avoids empty labels, “Tasks (0)”, or stale content). */
const ViewEditTaskModalBodySkeleton = () => (
  <Grid
    container
    sx={{
      flex: 1,
      minHeight: 0,
      height: '100%',
      flexWrap: 'nowrap',
      overflow: 'hidden',
      backgroundColor: '#F2F2F2',
    }}
  >
    <Grid
      item
      sx={{
        width: { xs: '32%', md: '300px' },
        minWidth: { xs: '32%', md: '300px' },
        backgroundColor: '#F7F7F7',
        borderRight: '1px solid #D7D7D7',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2.25,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflowY: 'auto',
        }}
      >
        <Stack spacing={2}>
          {Array.from({ length: 7 }, (_, i) => (
            <Box
              key={i}
              sx={{
                display: 'grid',
                gridTemplateColumns: '56px minmax(0, 1fr)',
                columnGap: 1,
                alignItems: 'center',
              }}
            >
              <Skeleton variant="text" width={36 + (i % 3) * 8} height={20} />
              <Skeleton variant="rounded" height={20} sx={{ borderRadius: 1, maxWidth: '100%' }} />
            </Box>
          ))}
        </Stack>
        <Box sx={{ pt: 1 }}>
          <Skeleton variant="text" width={88} height={22} sx={{ mb: 0.75 }} />
          <Stack spacing={0.75}>
            <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1 }} />
          </Stack>
        </Box>
      </Box>
    </Grid>

    <Grid
      item
      sx={{
        flex: '1 1 auto',
        minWidth: 0,
        minHeight: 0,
        m: 2,
        p: 0,
        overflowY: 'auto',
      }}
    >
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', px: 2 }}>
          <Skeleton variant="rounded" width={108} height={28} sx={{ borderRadius: '14px' }} />
          <Skeleton variant="rounded" width={132} height={28} sx={{ borderRadius: '14px' }} />
          <Skeleton variant="rounded" width={96} height={28} sx={{ borderRadius: '14px' }} />
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: '8px',
            border: '1px solid #D9D9D9',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Stack spacing={1.5}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(3, 1fr)',
                },
                gap: 1.25,
              }}
            >
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={40} />
            </Box>
            <Skeleton variant="text" height={28} width="85%" />
            <Skeleton variant="text" height={22} width="95%" />
            <Skeleton variant="text" height={22} width="72%" />
            <Skeleton variant="rounded" width={160} height={36} sx={{ borderRadius: 1, alignSelf: 'flex-start' }} />
            <Skeleton variant="rounded" height={100} sx={{ borderRadius: 1 }} />
            <Skeleton variant="text" width={120} height={22} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              <Skeleton variant="rounded" width={140} height={28} sx={{ borderRadius: '14px' }} />
              <Skeleton variant="rounded" width={120} height={28} sx={{ borderRadius: '14px' }} />
            </Box>
          </Stack>
        </Paper>
      </Stack>
    </Grid>
  </Grid>
);

const recipientChipSx = {
  height: 28,
  borderRadius: '14px',
  backgroundColor: '#FFFFFF',
  border: '1px solid #D8D8D8',
  maxWidth: '100%',
  '& .MuiChip-label': {
    pl: 0.75,
    pr: 1,
    fontSize: '13px',
  },
  '& .MuiChip-icon': {
    ml: 1,
    mr: 0.75,
  },
} as const;

const RecipientChip = ({
  recipient,
  disableTooltip = false,
}: {
  recipient: ITaskChainRecipient;
  /** Use inside the info popover where status text is shown beside/below the chip */
  disableTooltip?: boolean;
}) => {
  const chip = (
    <Chip
      size="small"
      label={getRecipientChipLabel(recipient)}
      icon={
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: getStatusColor(recipient.statusId) }} />
      }
      sx={recipientChipSx}
    />
  );

  if (disableTooltip) {
    return chip;
  }

  return (
    <Tooltip
      title={getRecipientStatusHistoryText(recipient)}
      placement="bottom"
      enterDelay={400}
      componentsProps={recipientChipTooltipComponentsProps}
    >
      {chip}
    </Tooltip>
  );
};

const AttachmentTile = ({
  attachment,
  showDeleteButton,
  deleteDisabled,
  onDelete,
}: {
  attachment: ITaskChainAttachment;
  showDeleteButton?: boolean;
  deleteDisabled?: boolean;
  onDelete?: () => void;
}) => {
  const icon = getAttachmentIcon(getAttachmentExtension(attachment));

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1.25,
        py: 1,
        border: '1px solid #D9D9D9',
        borderRadius: '4px',
        backgroundColor: '#FFFFFF',
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
        {icon ? (
          <Box component="img" src={icon} alt="Attachment icon" sx={{ width: 18, height: 18, flexShrink: 0 }} />
        ) : (
          <AttachFileIcon sx={{ fontSize: 18, color: '#7A7A7A', flexShrink: 0 }} />
        )}
        <Typography
          variant="body2"
          sx={{
            fontSize: '13px',
            lineHeight: '18px',
            color: '#333333',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
          }}
          onClick={() => window.open(attachment.sasAttachmentUrl, '_blank')}
        >
          {getAttachmentDisplayName(attachment)}
        </Typography>
      </Stack>
      {showDeleteButton && (
        <IconButton
          size="small"
          disabled={deleteDisabled}
          onClick={onDelete}
          aria-label="Delete attachment"
          sx={{
            color: '#757575',
            flexShrink: 0,
            '&.Mui-disabled': { color: '#BDBDBD' },
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

const ToggleOption = ({
  checked,
  label,
  disabled,
  onChange,
}: {
  checked: boolean;
  label: string;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <Checkbox
      checked={checked}
      disabled={disabled}
      onChange={event => onChange(event.target.checked)}
      sx={{
        p: 0,
        '& .MuiSvgIcon-root': {
          fontSize: 22,
        },
      }}
    />
    <Typography sx={{ fontSize: '14px', color: disabled ? '#A5A5A5' : '#333333' }}>{label}</Typography>
  </Stack>
);

const isFollowUpDateInPast = (followupDateYmd: string) => {
  if (!followupDateYmd) return false;
  const picked = new Date(`${followupDateYmd}T12:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  picked.setHours(0, 0, 0, 0);
  return picked < today;
};

type ITaskChainStatusPickerProps = {
  formState: ITaskFormState;
  disabled: boolean;
  onBehalfRows: ITaskChainRecipient[];
  onPick: (recipientId: number, statusId: number) => void;
};

const TaskChainStatusPicker = ({ formState, disabled, onBehalfRows, onPick }: ITaskChainStatusPickerProps) => {
  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLElement | null>(null);
  /** Left-column selection in the two-panel "on behalf of" popover */
  const [panelRecipientId, setPanelRecipientId] = useState<number | null>(null);

  const hasOnBehalfUi = onBehalfRows.length > 0;

  const panelRecipient = useMemo(() => {
    if (panelRecipientId == null) return null;
    return onBehalfRows.find(r => r.recipientId === panelRecipientId) ?? null;
  }, [onBehalfRows, panelRecipientId]);

  const closeStatusMenu = () => {
    setStatusAnchorEl(null);
    setPanelRecipientId(null);
  };

  const handlePickStatus = (recipientId: number, statusId: number) => {
    onPick(recipientId, statusId);
    closeStatusMenu();
  };

  const selectedStatusForRecipient = (recipient: ITaskChainRecipient) =>
    formState.selectedRecipientId === recipient.recipientId ? formState.selectedStatusId : recipient.statusId;

  const renderStatusMenuItems = (recipient: ITaskChainRecipient | null) => {
    if (!recipient) return null;
    // On-behalf-of panel surfaces the 5-option list (incl. "Completed (Only My Task)") in the design-specified order.
    const optionsToRender = hasOnBehalfUi ? onBehalfStatusOptions : extendedStatusOptions;
    return optionsToRender.map(option => {
      const current = selectedStatusForRecipient(recipient);
      const selected = current === option.value;
      return (
        <MenuItem
          key={`${recipient.recipientId}-${option.value}`}
          selected={selected}
          onClick={() => handlePickStatus(recipient.recipientId, option.value)}
          sx={{ gap: 0.75, alignItems: 'center' }}
        >
          {selected ? <CheckIcon sx={{ fontSize: 18, color: '#1593D1' }} /> : <Box sx={{ width: 18, flexShrink: 0 }} />}
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: getStatusColor(option.value),
              flexShrink: 0,
            }}
          />
          <Typography component="span" sx={{ fontSize: '14px' }}>
            {option.label}
          </Typography>
        </MenuItem>
      );
    });
  };

  const openStatusMenu = (el: HTMLElement) => {
    if (disabled) return;
    if (onBehalfRows.length > 0) {
      const match = onBehalfRows.find(r => r.recipientId === formState.selectedRecipientId);
      setPanelRecipientId(match?.recipientId ?? onBehalfRows[0].recipientId);
    }
    setStatusAnchorEl(el);
  };

  const statusButtonAriaLabel = hasOnBehalfUi
    ? `${textLabels.status}: ${getStatusLabel(formState.selectedStatusId)}. ${textLabels.onBehalfOf}`
    : undefined;

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={statusButtonAriaLabel}
        aria-haspopup="listbox"
        aria-expanded={Boolean(statusAnchorEl)}
        onClick={event => {
          if (disabled) return;
          event.stopPropagation();
          openStatusMenu(event.currentTarget);
        }}
        onKeyDown={event => {
          if (disabled) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            openStatusMenu(event.currentTarget as HTMLElement);
          }
        }}
        sx={{
          /** Row underline (label + field) is on the parent Stack when pickable */
          minHeight: 40,
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          borderRadius: 0,
          px: '12px',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.55 : 1,
          outline: 'none',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ py: '8px', px: 0, width: '100%', minWidth: 0 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                flexShrink: 0,
                backgroundColor: getStatusColor(formState.selectedStatusId),
              }}
            />
            <Typography
              sx={{ fontSize: '14px', color: '#333333', flex: 1, minWidth: 0 }}
              noWrap
            >
              {getStatusLabel(formState.selectedStatusId)}
            </Typography>
          </Stack>
          {!disabled && (
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#666666', flexShrink: 0 }} aria-hidden />
          )}
        </Stack>
      </Box>

      <Popover
        open={Boolean(statusAnchorEl)}
        anchorEl={statusAnchorEl}
        onClose={closeStatusMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            maxHeight: 420,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {hasOnBehalfUi ? (
          <Stack direction="row" sx={{ minHeight: 200, maxHeight: 400 }}>
            <Box
              sx={{
                minWidth: 200,
                maxWidth: 240,
                borderRight: '1px solid',
                borderColor: 'divider',
                overflow: 'auto',
                py: 0.5,
              }}
            >
              <Typography sx={{ px: 1.5, py: 0.75, fontWeight: 400, fontSize: '13px' }}>{textLabels.onBehalfOf}</Typography>
              {onBehalfRows.map(recipient => {
                const isPanelRow = panelRecipientId === recipient.recipientId;
                return (
                  <MenuItem
                    key={recipient.recipientId}
                    dense
                    selected={isPanelRow}
                    onMouseEnter={() => setPanelRecipientId(recipient.recipientId)}
                    onFocus={() => setPanelRecipientId(recipient.recipientId)}
                    onClick={() => setPanelRecipientId(recipient.recipientId)}
                    sx={{
                      fontSize: '14px',
                      justifyContent: 'space-between',
                      gap: 1,
                      ...(isPanelRow ? { backgroundColor: 'action.selected' } : {}),
                    }}
                  >
                    <Typography component="span" sx={{ fontSize: '14px', fontWeight: isPanelRow ? 700 : 400 }}>
                      {getRecipientChipLabel(recipient)}
                    </Typography>
                    {isPanelRow ? <ChevronRightIcon sx={{ fontSize: 20, color: 'action.active' }} /> : null}
                  </MenuItem>
                );
              })}
            </Box>
            <Box sx={{ minWidth: 280, flex: 1, overflow: 'auto', py: 0.5 }}>
              <Typography sx={{ px: 1.5, py: 0.75, fontWeight: 600, fontSize: '13px' }}>{textLabels.setStatus}</Typography>
              <Box sx={{ py: 0.5 }}>{panelRecipient ? renderStatusMenuItems(panelRecipient) : null}</Box>
            </Box>
          </Stack>
        ) : (
          <Box sx={{ py: 0.5, minWidth: 280, maxHeight: 360, overflow: 'auto' }}>
            <Typography sx={{ px: 1.5, py: 0.75, fontWeight: 600, fontSize: '13px' }}>{textLabels.setStatus}</Typography>
            <Stack component="span" sx={{ py: 0.5 }}>
              {extendedStatusOptions.map(option => {
                const selected = formState.selectedStatusId === option.value;
                return (
                  <MenuItem
                    key={option.value}
                    selected={selected}
                    onClick={() => handlePickStatus(formState.selectedRecipientId, option.value)}
                    sx={{ gap: 0.75, alignItems: 'center' }}
                  >
                    {selected ? <CheckIcon sx={{ fontSize: 18, color: '#1593D1' }} /> : <Box sx={{ width: 18, flexShrink: 0 }} />}
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(option.value),
                        flexShrink: 0,
                      }}
                    />
                    <Typography component="span" sx={{ fontSize: '14px' }}>
                      {option.label}
                    </Typography>
                  </MenuItem>
                );
              })}
            </Stack>
          </Box>
        )}
      </Popover>
    </>
  );
};

export const ViewEditTaskModal = ({
  taskID,
  open,
  handleClose,
  showNavigation = false,
  onNavigatePrevious,
  onNavigateNext,
  disableNavigatePrevious = false,
  disableNavigateNext = false,
  onTaskPersistSuccess,
  applyNotificationOpenReadBehavior = false,
}: {
  taskID?: number;
  open: boolean;
  /** Pass `{ refreshGrid: true }` after delete success so parents can refresh (e.g. Global Search grid). */
  handleClose: (options?: { refreshGrid?: boolean }) => void;
  showNavigation?: boolean;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  disableNavigatePrevious?: boolean;
  disableNavigateNext?: boolean;
  /** When set (e.g. task search grid), invoked after body or status save succeeds. */
  onTaskPersistSuccess?: () => void;
  /**
   * When true (header task notifications + new task UI), after the chain loads run the same POST `/task/update-status`
   * New→Pending read behavior as TaskDetails (self + coverage).
   */
  applyNotificationOpenReadBehavior?: boolean;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const taskChain = useSelector(selectTaskChainDetailsData);
  const isLoading = useSelector(selectIsTaskChainLoading);
  const isSaveLoading = useSelector(selectIsTaskChainSaveLoading);
  const isStatusLoading = useSelector(selectIsTaskChainStatusLoading);
  const taskChainError = useSelector(selectTaskChainError);
  const isTaskChainLoopOutLoading = useSelector(selectTaskChainLoopOutLoading);
  const taskChainLoopedOut = useSelector(selectTaskChainLoopedOut);
  const taskChainUpdatedId = useSelector(selectTaskChainUpdatedId);
  const taskChainStatusUpdated = useSelector(selectTaskChainStatusUpdated);
  const taskChainDeleteSucceeded = useSelector(selectTaskChainDeleteSucceeded);
  const isTaskChainDeleteLoading = useSelector(selectTaskChainDeleteLoading);
  const user = useSelector(selectUser);
  const activeCoverage = useActiveCoverageForTaskActions();
  const currentUserEmployeeId = user?.userInfo?.employeeId;
  const [expanded, setExpanded] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ITaskChainAttachment | null>(null);
  const [taskPendingPermanentDelete, setTaskPendingPermanentDelete] = useState<ITaskChainItem | null>(null);
  const [locallyDeletedAttachments, setLocallyDeletedAttachments] = useState<ITaskChainAttachment[]>([]);
  const [descriptionExpandedByTask, setDescriptionExpandedByTask] = useState<Record<number, boolean>>({});
  const [initialStateByTask, setInitialStateByTask] = useState<ITaskFormStateMap>({});
  const [formStateByTask, setFormStateByTask] = useState<ITaskFormStateMap>({});
  const [activeTaskId, setActiveTaskId] = useState<number | null>(null);
  const [replyTargetTaskId, setReplyTargetTaskId] = useState<number | null>(null);
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);
  const [focusedTaskId, setFocusedTaskId] = useState<number | null>(null);
  const skipNextTaskChainErrorCloseRef = useRef(false);
  const hasSeenTaskChainNonErrorSinceOpenRef = useRef(false);
  const taskCardRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const notificationReadAppliedForOpenRef = useRef<number | null>(null);
  const formInitSourceRef = useRef<{
    taskChain: ITaskChainItem | null | undefined;
    taskID?: number;
    employeeId?: number;
  } | null>(null);
  const persistCallbackArmedRef = useRef(false);
  const searchBrandsFetchedForRef = useRef<number | null>(null);
  const [candidateSearchBrands, setCandidateSearchBrands] = useState<Array<{ name: string; brandId: number | string }>>([]);
  const [isSearchBrandsLoaded, setIsSearchBrandsLoaded] = useState(false);

  const activeCoverageFormSyncKey = useMemo(
    () => serializeActiveCoverageForForm(activeCoverage),
    [activeCoverage],
  );

  useEffect(() => {
    if (open && taskID) {
      skipNextTaskChainErrorCloseRef.current = true;
      hasSeenTaskChainNonErrorSinceOpenRef.current = false;
      dispatch(taskDetailsActions.setTaskChainUpdatedId(undefined));
      dispatch(taskDetailsActions.setTaskChainStatusUpdated(false));
      dispatch(taskDetailsActions.setTaskChainDeleteSucceeded(false));
      dispatch(taskDetailsActions.setTaskChainDeleteLoading(false));
      dispatch(taskDetailsActions.getTaskChainDetailsAction(taskID));
      dispatch(coverageActions.getActiveCoverageProvidedByUser());
    }
  }, [dispatch, open, taskID]);

  useEffect(() => {
    if (!open || !onTaskPersistSuccess || !persistCallbackArmedRef.current) return;

    const bodySaveSucceeded =
      taskChainUpdatedId !== undefined && taskChainUpdatedId !== -1;
    const statusSaveSucceeded = taskChainStatusUpdated === true;
    if (!bodySaveSucceeded && !statusSaveSucceeded) return;

    persistCallbackArmedRef.current = false;
    onTaskPersistSuccess();
    dispatch(taskDetailsActions.setTaskChainUpdatedId(undefined));
    dispatch(taskDetailsActions.setTaskChainStatusUpdated(false));
  }, [dispatch, open, onTaskPersistSuccess, taskChainUpdatedId, taskChainStatusUpdated]);

  useEffect(() => {
    if (!taskChainDeleteSucceeded) return;

    // Always clear the delete success flag, even when modal is closed,
    // so a late saga completion cannot auto-close the next modal open.
    dispatch(taskDetailsActions.setTaskChainDeleteSucceeded(false));

    if (!open) return;
    // Delete success refresh is coordinated by the saga-level refresh nonce.
    // Avoid firing onTaskPersistSuccess here to prevent stale close-time refresh flags.
    handleClose();
  }, [dispatch, open, taskChainDeleteSucceeded, onTaskPersistSuccess, handleClose]);

  useEffect(() => {
    if (!open) {
      setExpanded(false);
      setReplyTargetTaskId(null);
      setDeleteTarget(null);
      setTaskPendingPermanentDelete(null);
      notificationReadAppliedForOpenRef.current = null;
      formInitSourceRef.current = null;
      persistCallbackArmedRef.current = false;
      dispatch(taskDetailsActions.setTaskChainDeleteSucceeded(false));
      dispatch(taskDetailsActions.setTaskChainDeleteLoading(false));
      setCandidateSearchBrands([]);
      setIsSearchBrandsLoaded(false);
      searchBrandsFetchedForRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (!open || isLoading || !taskChain?.candidate?.id) return;

    const candidateId = taskChain.candidate.id;
    if (searchBrandsFetchedForRef.current === candidateId) return;

    setCandidateSearchBrands([]);
    setIsSearchBrandsLoaded(false);

    let cancelled = false;
    const fetchCandidateBrands = async () => {
      try {
        const result = await getAMIECategorySearch({
          searchType: SearchType.candidate,
          pageNumber: 1,
          pageSize: 20,
          candidateIds: [String(candidateId)],
        }, undefined);
        if (!cancelled) {
          if (result?.items) {
            setCandidateSearchBrands(
              result.items
                .filter((item: any) => item.brand && item.brandId)
                .map((item: any) => ({ name: item.brand, brandId: item.brandId })),
            );
          }
          setIsSearchBrandsLoaded(true);
          searchBrandsFetchedForRef.current = candidateId;
        }
      } catch {
        if (!cancelled) {
          setIsSearchBrandsLoaded(true);
          searchBrandsFetchedForRef.current = candidateId;
        }
      }
    };

    fetchCandidateBrands();
    return () => { cancelled = true; };
  }, [open, isLoading, taskChain?.candidate?.id]);

  useEffect(() => {
    if (!open || !applyNotificationOpenReadBehavior || !taskID || taskID <= 0) return;
    if (isLoading || !taskChain) return;

    const flat = flattenTaskChain(taskChain);
    if (!flat.length || !flat.some(t => t.taskId === taskID)) return;
    if (notificationReadAppliedForOpenRef.current === taskID) return;
    notificationReadAppliedForOpenRef.current = taskID;

    void (async () => {
      const requests = buildNotificationOpenReadStatusRequests(flat, user, activeCoverage);
      if (!requests.length) return;

      let anyOk = false;
      for (const body of requests) {
        try {
          const res = await updateTaskChainStatus(body);
          if (
            res &&
            httpSuccess(res.status) &&
            (res.data?.failures?.length ?? 0) === 0
          ) {
            anyOk = true;
          }
        } catch {
          // Same as TaskDetails setReadStatus: service layer already tracks exceptions
        }
      }
      if (anyOk) {
        dispatch(taskDetailsActions.getTaskChainDetailsAction(taskID));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `activeCoverageFormSyncKey` is the value dependency; `activeCoverage` would re-fire on reference-only Redux updates
  }, [
    activeCoverageFormSyncKey,
    applyNotificationOpenReadBehavior,
    dispatch,
    isLoading,
    open,
    taskChain,
    taskID,
    user,
  ]);

  useEffect(() => {
    if (!open) return;

    if (!taskChainError) {
      hasSeenTaskChainNonErrorSinceOpenRef.current = true;
      return;
    }

    if (skipNextTaskChainErrorCloseRef.current && !hasSeenTaskChainNonErrorSinceOpenRef.current) {
      skipNextTaskChainErrorCloseRef.current = false;
      return;
    }

    setLocallyDeletedAttachments([]);
    handleClose();
  }, [open, taskChainError, handleClose]);

  useEffect(() => {
    if (!taskChainLoopedOut) return;

    // Always clear the loop-out success flag, even when modal is closed,
    // so a late saga completion cannot auto-close the next modal open.
    dispatch(taskDetailsActions.setTaskChainLoopedOut(false));

    if (!open) return;

    setShowDiscard(false);
    setLocallyDeletedAttachments([]);
    handleClose();
  }, [dispatch, handleClose, open, taskChainLoopedOut]);

  /**
   * Scroll the focused task card into view. Re-run when `taskChain` updates too (e.g. after save) so we
   * still scroll even if `focusedTaskId` is unchanged — the prior effect missed that case.
   */
  useLayoutEffect(() => {
    if (!open || focusedTaskId == null) return;

    const scrollToFocused = () => {
      const node = taskCardRefs.current[focusedTaskId];
      node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    scrollToFocused();
    const raf = requestAnimationFrame(scrollToFocused);

    return () => cancelAnimationFrame(raf);
  }, [focusedTaskId, open, taskChain]);

  useEffect(() => {
    if (!taskChain || !open) return;

    const flattenedTasks = flattenTaskChain(taskChain);
    const nextMap = flattenedTasks.reduce<ITaskFormStateMap>((acc, task) => {
      acc[task.taskId] = toFormState(task, currentUserEmployeeId, activeCoverage);
      return acc;
    }, {});
    const selectedTaskId =
      (taskID && flattenedTasks.some(task => task.taskId === taskID) ? taskID : undefined) ||
      flattenedTasks[0]?.taskId ||
      null;

    const prevSource = formInitSourceRef.current;
    /** Opening task / parent task id or viewer context changed — reset selection to the opened task. */
    const identityReset =
      !prevSource ||
      prevSource.taskID !== taskID ||
      prevSource.employeeId !== currentUserEmployeeId;
    /**
     * Redux replaced the chain after a refetch (e.g. save). Do not treat like a new open — keep the
     * user on the task card they edited; only `identityReset` should jump focus to `taskID`.
     */
    const chainDataRefreshed =
      !!prevSource && !identityReset && prevSource.taskChain !== taskChain;

    formInitSourceRef.current = { taskChain, taskID, employeeId: currentUserEmployeeId };

    if (identityReset) {
      setInitialStateByTask(nextMap);
      setFormStateByTask(nextMap);
      setActiveTaskId(selectedTaskId);
      setFocusedTaskId(selectedTaskId);
      setReplyTargetTaskId(null);
      setLocallyDeletedAttachments([]);
      setDescriptionExpandedByTask({});
      return;
    }

    if (chainDataRefreshed) {
      setInitialStateByTask(nextMap);
      setFormStateByTask(nextMap);
      setActiveTaskId(prev =>
        prev != null && flattenedTasks.some(t => t.taskId === prev) ? prev : selectedTaskId,
      );
      setFocusedTaskId(prev =>
        prev != null && flattenedTasks.some(t => t.taskId === prev) ? prev : selectedTaskId,
      );
      return;
    }

    const mergeRecipientFields = (prev: ITaskFormStateMap): ITaskFormStateMap => {
      let changed = false;
      const merged = { ...prev };
      for (const task of flattenedTasks) {
        const fresh = nextMap[task.taskId];
        const existing = prev[task.taskId];
        if (!existing) {
          merged[task.taskId] = fresh;
          changed = true;
          continue;
        }
        if (
          existing.selectedRecipientId !== fresh.selectedRecipientId ||
          existing.selectedStatusId !== fresh.selectedStatusId
        ) {
          merged[task.taskId] = {
            ...existing,
            selectedRecipientId: fresh.selectedRecipientId,
            selectedStatusId: fresh.selectedStatusId,
          };
          changed = true;
        }
      }
      return changed ? merged : prev;
    };

    setInitialStateByTask(mergeRecipientFields);
    setFormStateByTask(mergeRecipientFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `activeCoverageFormSyncKey` tracks coverage payload; `activeCoverage` in the body must not be a dep or equivalent arrays reset the form on every saga completion
  }, [taskChain, open, taskID, currentUserEmployeeId, activeCoverageFormSyncKey]);

  const flatTasks = useMemo(() => flattenTaskChain(taskChain), [taskChain]);
  const activeTask = useMemo(
    () => flatTasks.find(task => task.taskId === activeTaskId) || null,
    [activeTaskId, flatTasks],
  );
  const replyTargetTask = useMemo(
    () => flatTasks.find(task => task.taskId === replyTargetTaskId) || null,
    [flatTasks, replyTargetTaskId],
  );
  const replyParentTaskDetails = useMemo(
    () => (replyTargetTask ? toReplyTaskDetails(replyTargetTask) : undefined),
    [replyTargetTask],
  );
  const activeFormState = activeTaskId ? formStateByTask[activeTaskId] : null;

  const isDirty = useMemo(
    () =>
      Object.keys(formStateByTask).some(
        taskId =>
          JSON.stringify(formStateByTask[Number(taskId)]) !== JSON.stringify(initialStateByTask[Number(taskId)]),
      ),
    [formStateByTask, initialStateByTask],
  );

  const requiredValid = useMemo(() => {
    if (!flatTasks.length) return false;
    const editableTasks = flatTasks.filter(task =>
      canViewerEditTaskChainTaskFields(currentUserEmployeeId, task, activeCoverage),
    );
    if (!editableTasks.length) return true;
    return editableTasks.every(task => {
      const state = formStateByTask[task.taskId];
      if (!state) return false;
      // Subject is read-only in this modal; do not block Save when API data has empty/whitespace subject.
      return (!state.followupDateEditable || !!state.followupDate) && state.priorityId > 0;
    });
  }, [activeCoverage, currentUserEmployeeId, flatTasks, formStateByTask]);

  const isPersistingTaskChain = isLoading || isSaveLoading || isStatusLoading;
  const controlsEnabled = !isPersistingTaskChain;

  const activeTaskHasSaveableChanges = useMemo(() => {
    if (activeTaskId == null || !activeTask || !activeFormState) return false;
    const initial = initialStateByTask[activeTaskId];
    if (!initial) return false;
    const { shouldUpdateStatus, bodyDirty } = computeTaskSaveWork(
      activeTask,
      activeFormState,
      initial,
      flatTasks,
    );
    const canPersistStatus =
      shouldUpdateStatus &&
      canViewerEditTaskChainTaskFields(currentUserEmployeeId, activeTask, activeCoverage);
    return canPersistStatus || bodyDirty;
  }, [activeCoverage, activeFormState, activeTask, activeTaskId, currentUserEmployeeId, flatTasks, initialStateByTask]);

  const canSave = !isPersistingTaskChain && requiredValid && activeTaskHasSaveableChanges;
  const showDetailsSkeleton = isLoading;
  const brandId = user?.userInfo?.companyId;
  const currentEmployeeId = user?.userInfo?.employeeId;
  const canDeleteSidebarAttachment = useCallback(
    (attachment: ITaskChainAttachment) => {
      const sourceTask = flatTasks.find(t => t.taskId === attachment.taskId);
      if (!sourceTask) return false;
      return (
        isCurrentUserTaskSender(currentEmployeeId, sourceTask.sender) ||
        isCurrentUserCoveringSender(sourceTask) ||
        isUserCoveringTaskSender(sourceTask, activeCoverage)
      );
    },
    [flatTasks, currentEmployeeId, activeCoverage],
  );
  const attachments = useMemo(() => collectAttachmentsFromTaskChain(flatTasks), [flatTasks]);
  const activeAttachments = useMemo(
    () => sortAttachmentsAlphabetically(attachments.filter(attachment => !isDeletedAttachment(attachment))),
    [attachments],
  );
  const placementStartDate = formatShortDate(taskChain?.placementStartDate);
  const placementEndDate = formatShortDate(taskChain?.placementEndDate);
  const placementDateRangeText =
    placementStartDate || placementEndDate
      ? `${placementStartDate || '-'} - ${placementEndDate || '-'}`
      : undefined;
  const placementWeeksLabel = taskChain?.placementDurationWeeks
    ? `(${taskChain.placementDurationWeeks} ${taskChain.placementDurationWeeks === 1 ? 'week' : 'weeks'})`
    : undefined;
  const taskCategoryChipText = useMemo(() => {
    const firstAvailableCategory = [
      taskChain?.taskCategory,
      taskChain?.category,
      activeTask?.taskCategory,
      activeTask?.category,
      ...flatTasks.map(task => task.taskCategory),
      ...flatTasks.map(task => task.category),
    ]
      .map(value => value?.trim())
      .find(value => !!value);

    return firstAvailableCategory || '';
  }, [activeTask?.category, activeTask?.taskCategory, flatTasks, taskChain?.category, taskChain?.taskCategory]);

  const isCandidateOnlyTask = !!taskChain?.candidate?.id && !taskChain?.placementId;
  const focusedTask = flatTasks.find(task => task.taskId === focusedTaskId) || activeTask;
  const focusedTaskRecipients = focusedTask?.recipients || [];
  const { classes, cx } = viewEditTaskModalStyles({ expanded });

  const sidebarRows = useMemo<ISidebarRow[]>(
    () => {
      const rows: ISidebarRow[] = [];
      const hasCandidate = !!taskChain?.candidate?.id && !!taskChain?.candidate?.name;
      const hasPlacement = !!taskChain?.placementId;
      const hasOrder = !!taskChain?.orderId;
      const hasUnit = !!taskChain?.unit?.id || !!taskChain?.unit?.name;
      const hasFacility = !!taskChain?.facility?.id || !!taskChain?.facility?.name;
      const showFacilityGroup = hasPlacement || hasOrder || hasUnit || hasFacility;
      const contractAffiliateIdNum = taskChain?.contractAffiliateId ?? 0;
      const isContractAffiliateProfileTask =
        contractAffiliateIdNum > 0 && !hasPlacement && !hasOrder && !hasFacility && !hasUnit;
      // Aligns with GetTaskDetailsHandler.UpdateTaskSource — facility profile tasks are Facility-sourced (not order/placement)
      const placementIdNum = taskChain?.placementId ?? 0;
      const orderIdNum = taskChain?.orderId ?? 0;
      const facilityIdNum = taskChain?.facility?.id ?? 0;
      const candidateIdNum = taskChain?.candidate?.id ?? 0;
      const isPlacementTaskSource =
        placementIdNum > 0 && orderIdNum > 0 && facilityIdNum > 0 && candidateIdNum > 0;
      const isOrderTaskSource = orderIdNum > 0 && facilityIdNum > 0;
      const isFacilityProfileTask =
        facilityIdNum > 0 && !isPlacementTaskSource && !isOrderTaskSource;
      const placementBrandName = normalizePlacementBrand(taskChain?.brandPlacement);

      const resolvedNonCandidateBrandId = (() => {
        if (isCandidateOnlyTask || !placementBrandName) return brandId;
        const match = candidateSearchBrands.find(
          b => b.name?.toLowerCase().trim() === placementBrandName.toLowerCase().trim(),
        );
        return match?.brandId || brandId;
      })();

      const candidateValue =
        hasCandidate
          ? isCandidateOnlyTask
            ? `${taskChain.candidate.name} (${taskChain.candidate.id})`
            : resolvedNonCandidateBrandId
              ? (
                <RouterLink to={`/candidate/${taskChain.candidate.id}/${resolvedNonCandidateBrandId}`} className={classes.link}>
                  {`${taskChain.candidate.name} (${taskChain.candidate.id})`}
                </RouterLink>
                )
              : (
                `${taskChain.candidate.name} (${taskChain.candidate.id})`
                )
          : '-';

      const brandValue = (() => {
        if (hasPlacement) {
          return placementBrandName || '-';
        }

        if (!isCandidateOnlyTask) return '-';

        if (!isSearchBrandsLoaded) {
          return <Skeleton variant="text" width={120} sx={{ fontSize: '13px' }} />;
        }

        if (candidateSearchBrands.length > 0) {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {candidateSearchBrands.map((brand, index) => {
                const key = `${brand.name}-${brand.brandId || index}`;
                if (taskChain?.candidate?.id && brand.brandId) {
                  return (
                    <RouterLink
                      key={key}
                      to={`/candidate/${taskChain.candidate.id}/${brand.brandId}`}
                      className={classes.link}
                    >
                      {brand.name}
                    </RouterLink>
                  );
                }

                return (
                  <Typography key={key} component="span" sx={{ fontSize: '13px', lineHeight: '20px', color: '#333333' }}>
                    {brand.name}
                  </Typography>
                );
              })}
            </Box>
          );
        }

        return '-';
      })();

      if (hasCandidate) {
        rows.push({
          label: 'CID',
          value: candidateValue,
        });
      }

      if (hasPlacement) {
        const placementSkillsetText = taskChain.placementSkillset?.trim();
        const placementStatusText = taskChain.placementStatus?.trim();
        const pidSecondary =
          placementDateRangeText ||
          placementWeeksLabel ||
          placementSkillsetText ||
          placementStatusText ? (
            <Stack spacing={0.5}>
              {placementDateRangeText ? (
                <Typography component="div" sx={{ fontSize: '12px', lineHeight: '18px', color: '#666666' }}>
                  {placementDateRangeText}
                </Typography>
              ) : null}
              {placementWeeksLabel ? (
                <Typography
                  component="div"
                  sx={{ fontSize: '12px', lineHeight: '18px', color: '#666666', whiteSpace: 'nowrap' }}
                >
                  {placementWeeksLabel}
                </Typography>
              ) : null}
              {placementSkillsetText ? (
                <Typography component="div" sx={{ fontSize: '13px', lineHeight: '20px', color: '#333333' }}>
                  {placementSkillsetText}
                </Typography>
              ) : null}
              {placementStatusText ? (
                <Chip
                  label={placementStatusText}
                  size="small"
                  sx={{
                    alignSelf: 'flex-start',
                    height: 22,
                    maxWidth: '100%',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 700,
                    backgroundColor: '#EDE9FE',
                    color: '#5B21B6',
                    border: 'none',
                    '& .MuiChip-label': {
                      px: 1,
                      py: 0,
                      fontWeight: 700,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
              ) : null}
            </Stack>
          ) : undefined;

        rows.push({
          label: 'PID',
          value: (
            <RouterLink to={`/placement/${taskChain.placementId}`} className={classes.link}>
              {taskChain.placementId}
            </RouterLink>
          ),
          secondary: pidSecondary,
        });
      }

      if (hasPlacement || isCandidateOnlyTask) {
        rows.push({
          label: 'Brand',
          value: brandValue,
        });
      }

      if (hasPlacement || hasOrder) {
        const orderTypeLabel = taskChain?.orderType?.trim();
        rows.push({
          label: 'OID',
          value: taskChain?.orderId ? (
            <>
              <RouterLink to={`/order/${taskChain.orderId}`} className={classes.link}>
                {taskChain.orderId}
              </RouterLink>
              {orderTypeLabel ? ` | ${orderTypeLabel}` : null}
            </>
          ) : (
            '-'
          ),
        });
      }

      if (showFacilityGroup && !isFacilityProfileTask) {
        rows.push({
          label: 'UID',
          value: taskChain?.unit?.name
            ? taskChain.unit.id && taskChain?.facility?.id
              ? (
                <RouterLink
                  to={`/facility/${taskChain.facility.id}/unit/${taskChain.unit.id}`}
                  className={classes.link}
                >
                  {`${taskChain.unit.name} (${taskChain.unit.id})`}
                </RouterLink>
                )
              : taskChain.unit.id
                ? `${taskChain.unit.name} (${taskChain.unit.id})`
                : taskChain.unit.name
            : '-',
        });
      }

      if (showFacilityGroup) {
        const facilityAddressSecondary = formatFacilityAddressLine(
          taskChain?.facilityCity,
          taskChain?.facilityState,
          taskChain?.facilityPostalCode,
        );
        rows.push({
          label: 'FID',
          value:
            taskChain?.facility?.id && taskChain?.facility?.name ? (
              <RouterLink to={`/facility/${taskChain.facility.id}`} className={classes.link}>
                {`${taskChain.facility.name} (${taskChain.facility.id})`}
              </RouterLink>
            ) : (
              '-'
            ),
          secondary: facilityAddressSecondary,
        });
      }

      if (showFacilityGroup || isContractAffiliateProfileTask) {
        const contractAffiliateName = taskChain?.contractAffiliateName?.trim();
        const contractAffiliateId = taskChain?.contractAffiliateId;
        const hasContractAffiliateId =
          contractAffiliateId != null && contractAffiliateId > 0;
        const caidDisplay =
          contractAffiliateName && hasContractAffiliateId
            ? `${contractAffiliateName} (${contractAffiliateId})`
            : contractAffiliateName || (hasContractAffiliateId ? String(contractAffiliateId) : '-');
        rows.push({
          label: 'CAID',
          value: caidDisplay,
        });
      }

      if (showFacilityGroup || isContractAffiliateProfileTask) {
        rows.push({
          label: 'Division',
          value: taskChain?.division?.trim() || '-',
        });
      }

      return rows;
    },
    [
      isCandidateOnlyTask,
      brandId,
      candidateSearchBrands,
      isSearchBrandsLoaded,
      classes.link,
      placementDateRangeText,
      placementWeeksLabel,
      taskChain?.candidate?.id,
      taskChain?.candidate?.name,
      taskChain?.brandPlacement,
      taskChain?.contractAffiliateId,
      taskChain?.contractAffiliateName,
      taskChain?.division,
      taskChain?.facilityCity,
      taskChain?.facilityPostalCode,
      taskChain?.facilityState,
      taskChain?.facility?.id,
      taskChain?.facility?.name,
      taskChain?.orderId,
      taskChain?.orderType,
      taskChain?.placementId,
      taskChain?.placementSkillset,
      taskChain?.placementStatus,
      taskChain?.unit?.id,
      taskChain?.unit?.name,
    ],
  );

  const onCloseModal = () => {
    if (isDirty) {
      setShowDiscard(true);
      return;
    }

    setReplyTargetTaskId(null);
    setLocallyDeletedAttachments([]);
    handleClose();
  };

  const onConfirmDiscard = () => {
    setShowDiscard(false);
    setReplyTargetTaskId(null);
    setLocallyDeletedAttachments([]);
    handleClose();
  };

  const onDeleteAttachment = () => {
    if (!deleteTarget || !taskID) return;

    dispatch(
      taskDetailsActions.deleteTaskChainAttachmentAction({
        attachmentId: deleteTarget.taskAttachmentId,
        taskIdToRefresh: taskID,
      }),
    );

    setLocallyDeletedAttachments(previous => [...previous, deleteTarget]);
    setDeleteTarget(null);
  };

  const onSave = () => {
    if (!activeFormState || !taskID || !activeTask || activeTaskId == null || isPersistingTaskChain) return;

    const initial = initialStateByTask[activeTaskId];
    if (!initial) return;

    const rid = activeFormState.selectedRecipientId;
    const sid = activeFormState.selectedStatusId;

    const isOnBehalfOf =
      currentUserEmployeeId != null && currentUserEmployeeId > 0 && rid !== currentUserEmployeeId;

    const isEntireBranch = sid === STATUS_COMPLETED_ENTIRE_BRANCH;
    const isOnlyMyTask = sid === STATUS_COMPLETED_ONLY_MY_TASK;
    const statusIdToSend = (isEntireBranch || isOnlyMyTask) ? STATUS_COMPLETED : sid;
    // Maps to @UpdateCoveredEmployee. "Only My Task" => N. Otherwise Y so [dbo].[usp_UpdateTaskRecipientStatus]
    // runs the extra UPDATE for rows where CoveringEmployeeID = @RecipientID (coverer cascade). Non-coverage no-op.
    const updateCoveredEmployee = !isOnlyMyTask;

    const { shouldUpdateStatus, bodyDirty } = computeTaskSaveWork(
      activeTask,
      activeFormState,
      initial,
      flatTasks,
    );

    const canViewerPersistStatusForActiveTask = canViewerEditTaskChainTaskFields(
      currentUserEmployeeId,
      activeTask,
      activeCoverage,
    );
    const effectiveShouldUpdateStatus = shouldUpdateStatus && canViewerPersistStatusForActiveTask;
    const effectiveBodyDirty = bodyDirty;

    if (!effectiveShouldUpdateStatus && !effectiveBodyDirty) return;

    persistCallbackArmedRef.current = true;

    if (effectiveShouldUpdateStatus && effectiveBodyDirty) {
      dispatch(
        taskDetailsActions.persistTaskChainModalChangesAction({
          taskIdToRefresh: taskID,
          statusRequest: {
            rootTaskId: activeTask.rootTaskId,
            taskId: activeTask.taskId,
            applyToWholeChain: isEntireBranch,
            recipientId: rid,
            statusId: statusIdToSend,
            isOnBehalfOf,
            updateCoveredEmployee,
          },
          bodyRequest: {
            taskId: activeTask.taskId,
            assignedToIds: getToRecipients(activeTask.recipients).map(recipient => recipient.recipientId),
            description: activeFormState.text,
            ccIds: getCcRecipients(activeTask.recipients).map(recipient => recipient.recipientId),
            dueDate: toTaskChainDueDate(activeFormState),
            priorityId: activeFormState.priorityId,
            isOptionApproval: !!activeFormState.optionApproval,
            isFlagged: !!activeFormState.flag,
          },
        }),
      );
    } else if (effectiveShouldUpdateStatus) {
      dispatch(
        taskDetailsActions.updateTaskChainStatusAction({
          request: {
            rootTaskId: activeTask.rootTaskId,
            taskId: activeTask.taskId,
            applyToWholeChain: isEntireBranch,
            recipientId: rid,
            statusId: statusIdToSend,
            isOnBehalfOf,
            updateCoveredEmployee,
          },
          taskIdToRefresh: taskID,
        }),
      );
    } else if (effectiveBodyDirty) {
      dispatch(
        taskDetailsActions.saveTaskChainAction({
          request: {
            taskId: activeTask.taskId,
            assignedToIds: getToRecipients(activeTask.recipients).map(recipient => recipient.recipientId),
            description: activeFormState.text,
            ccIds: getCcRecipients(activeTask.recipients).map(recipient => recipient.recipientId),
            dueDate: toTaskChainDueDate(activeFormState),
            priorityId: activeFormState.priorityId,
            isOptionApproval: !!activeFormState.optionApproval,
            isFlagged: !!activeFormState.flag,
          },
          taskIdToRefresh: taskID,
        }),
      );
    }

    // Collapse UI-only status sentinels back to plain Completed (3) once we've dispatched, so the active card reflects what the SP persisted.
    if (effectiveShouldUpdateStatus && (isEntireBranch || isOnlyMyTask)) {
      setFormStateByTask(previous => {
        const prevRow = previous[activeTaskId];
        if (!prevRow) return previous;
        return {
          ...previous,
          [activeTaskId]: { ...prevRow, selectedStatusId: STATUS_COMPLETED },
        };
      });
    }
  };

  const onLoopOut = (task: ITaskChainItem) => {
    if (!taskID || isTaskChainLoopOutLoading) return;

    const loopOutRecipient = getLoopOutRecipientUnified(task, currentUserEmployeeId, activeCoverage);
    if (!loopOutRecipient) return;

    dispatch(
      taskDetailsActions.loopOutTaskChainAction({
        taskId: task.taskId,
        request: {
          recipientId: loopOutRecipient.recipientId,
          excludeRecipientIds: task.recipients
            .filter(recipient => !recipient.isActive && recipient.recipientId !== loopOutRecipient.recipientId)
            .map(recipient => recipient.recipientId),
        },
        taskIdToRefresh: taskID,
      }),
    );
  };

  const onConfirmPermanentDeleteTask = useCallback(() => {
    const pending = taskPendingPermanentDelete;
    setTaskPendingPermanentDelete(null);
    if (!pending || !taskID) return;
    dispatch(
      taskDetailsActions.deleteTaskChainTaskAction({
        taskIdToDelete: pending.taskId,
        chainRefreshTaskId: taskID,
      }),
    );
  }, [dispatch, taskPendingPermanentDelete, taskID]);

  const updateTaskFormState = (taskId: number, updater: (state: ITaskFormState) => ITaskFormState) => {
    setFormStateByTask(previous => {
      const current = previous[taskId];
      if (!current) return previous;
      return {
        ...previous,
        [taskId]: updater(current),
      };
    });
  };

  const onInfoIconOpen = (event: React.MouseEvent<HTMLElement>, taskId: number) => {
    setFocusedTaskId(taskId);
    setInfoAnchorEl(event.currentTarget);
  };

  const onInfoIconClose = () => {
    setInfoAnchorEl(null);
  };

  const onReply = (task: ITaskChainItem) => {
    setFocusedTaskId(task.taskId);
    setActiveTaskId(task.taskId);
    setReplyTargetTaskId(task.taskId);
  };

  const onReplyCreated = useCallback(() => {
    if (taskID) {
      dispatch(taskDetailsActions.getTaskChainDetailsAction(taskID));
    }
  }, [dispatch, taskID]);

  const onReplyModalClose = useCallback(() => {
    setReplyTargetTaskId(null);
  }, []);

  return (
    <>
      <GenericDialogWithNavigation
        open={open}
        fullWidth
        maxWidth="xl"
        draggable
        disableEnforceFocus
        variant="blue"
        className={cx(classes.modalContainer, 'task-chain-view-edit-modal')}
        onClose={onCloseModal}
        dialogTitleProps={{
          text: showDetailsSkeleton ? 'View Task' : taskChain?.subject || 'View Task',
          navigationArrows: showNavigation,
          onNavigateUp: onNavigatePrevious,
          onNavigateDown: onNavigateNext,
          disableNavigateUp: disableNavigatePrevious,
          disableNavigateDown: disableNavigateNext,
          closeButton: true,
          expandable: true,
          onExpandChange: expand => {
            setExpanded(expand);
          },
        }}
        dialogActions={[
          { text: 'Cancel', color: 'tertiary', onClick: onCloseModal },
          { text: 'Save', variant: 'contained', disabled: !canSave, onClick: onSave },
        ]}
      >
        {showDetailsSkeleton ? (
          <ViewEditTaskModalBodySkeleton />
        ) : (
          <Grid
            container
            sx={{
              flex: 1,
              minHeight: 0,
              height: '100%',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              overflow: 'hidden',
              backgroundColor: '#F2F2F2',
            }}
          >
            <Grid
              item
              sx={{
                width: { xs: '100%', sm: '32%', md: '300px' },
                minWidth: { xs: '100%', sm: '32%', md: '300px' },
                maxHeight: { xs: '42vh', sm: 'none' },
                backgroundColor: '#F7F7F7',
                borderRight: { xs: 'none', sm: '1px solid #D7D7D7' },
                borderBottom: { xs: '1px solid #D7D7D7', sm: 'none' },
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 2.25,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  overflowY: 'auto',
                }}
              >
                <Stack spacing={1.5}>
                  {sidebarRows.map(row => (
                    <SidebarRow key={row.label} {...row} />
                  ))}
                </Stack>

              {!!activeAttachments.length && (
                <Box sx={{ pt: 1 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#444444', mb: 0.75 }}>
                    {textLabels.attachments}
                  </Typography>
                  <Stack spacing={0.75}>
                    {activeAttachments.map(attachment => (
                      <AttachmentTile
                        key={`preview-${attachment.taskAttachmentId}`}
                        attachment={attachment}
                        showDeleteButton={canDeleteSidebarAttachment(attachment)}
                        deleteDisabled={!ENABLE_ATTACHMENT_DELETE || !controlsEnabled}
                        onDelete={() => setDeleteTarget(attachment)}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid
            item
            sx={{
              flex: '1 1 auto',
              minWidth: 0,
              minHeight: 0,
              m: 2,
              p: 0,
              overflowY: 'auto',
            }}
          >
            <Stack spacing={1.5}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  /** Horizontal inset matches task `Paper` padding (`p: 2`) */
                  px: 2,
                  pt: 0,
                  pb: 1.5,
                  backgroundColor: '#F2F2F2',
                  borderBottom: '1px solid #E5E5E5',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#333333' }}>
                    {`Tasks (${flatTasks.length})`}
                  </Typography>
                  {!!taskCategoryChipText && (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        minHeight: 32,
                        px: 1,
                        fontSize: '14px',
                        fontWeight: 600,
                        lineHeight: 1.25,
                        borderRadius: '4px',
                        backgroundColor: '#EFF7E8',
                        color: '#4D8F2F',
                      }}
                    >
                      {taskCategoryChipText}
                    </Box>
                  )}
                  {!!taskChain?.isFollowup && (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        minHeight: 32,
                        px: 1,
                        fontSize: '14px',
                        fontWeight: 600,
                        lineHeight: 1.25,
                        borderRadius: '4px',
                        backgroundColor: '#FFF3DF',
                        color: '#C37B1B',
                      }}
                    >
                      Follow-Up
                    </Box>
                  )}
                </Box>
              </Box>

              {flatTasks.map(task => {
                const formState = formStateByTask[task.taskId];
                if (!formState) return null;
                const loopOutRecipient = getLoopOutRecipientUnified(task, currentUserEmployeeId, activeCoverage);
                const onBehalfRows = getOnBehalfRecipientRowsUnified(task, currentEmployeeId, activeCoverage);
                const senderName = getEmployeeName(task.sender);
                const senderTimestamp = formatDateTime(task.sentDate);
                const detailsText = formState.text || '';
                const detailsHasOverflow =
                  detailsText.length > 380 || detailsText.split('\n').length > DESCRIPTION_PREVIEW_LINES;
                const descriptionExpanded = !!descriptionExpandedByTask[task.taskId];
                const isFocusedTask = activeTaskId === task.taskId;
                const canEditTaskFields = canViewerEditTaskChainTaskFields(
                  currentEmployeeId,
                  task,
                  activeCoverage,
                );
                const taskCardFocusEnabled = controlsEnabled && isFocusedTask;
                const taskFieldsEnabled = taskCardFocusEnabled && canEditTaskFields;
                const followUpDateEnabled = taskFieldsEnabled && formState.followupDateEditable;
                const followUpDateValue = formState.followupDateEditable
                  ? formState.followupDate || ''
                  : '--';
                const isTaskSender = isCurrentUserTaskSender(currentEmployeeId, task.sender);
                const isFollowUpPast = isFollowUpDateInPast(formState.followupDate);
                const activeTaskAttachments = latestAttachmentsByTaskAttachmentId(task.attachments).filter(
                  attachment => !isDeletedAttachment(attachment),
                );
                const deletedTaskAttachments = getDeletedAttachmentsForTask(task, locallyDeletedAttachments);
                const ccRecipients = getCcRecipients(task.recipients);
                const replyActionDisabled = !taskCardFocusEnabled || !ENABLE_REPLY_ACTION;
                const loopOutActionDisabled =
                  !taskCardFocusEnabled || !loopOutRecipient || isTaskChainLoopOutLoading;
                const deleteTaskActionDisabled =
                  !taskCardFocusEnabled ||
                  !task.canBeDeleted ||
                  !isTaskSender ||
                  isTaskChainLoopOutLoading ||
                  isTaskChainDeleteLoading;

                return (
                  <Paper
                    key={task.taskId}
                    elevation={0}
                    ref={node => {
                      taskCardRefs.current[task.taskId] = node;
                    }}
                    onClick={() => {
                      setActiveTaskId(task.taskId);
                      setFocusedTaskId(task.taskId);
                    }}
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      p: 2,
                      borderRadius: '8px',
                      border: isFocusedTask ? '1px solid #1593D1' : '1px solid #D9D9D9',
                      backgroundColor: '#FFFFFF',
                      cursor: 'pointer',
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            /** Min is full row (Status label + gap + picker); picker needs room for "Completed (Only My Task)" */
                            sm: 'minmax(360px, 1.85fr) auto minmax(104px, 0.72fr) minmax(128px, 0.88fr)',
                            /** Spacer column for wide layouts only */
                            lg: 'minmax(360px, 1.85fr) minmax(170px, 1.35fr) auto minmax(104px, 0.72fr) minmax(128px, 0.88fr)',
                          },
                          columnGap: 1.25,
                          rowGap: 1,
                          alignItems: 'end',
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.25}
                          alignItems="center"
                          sx={{
                            width: '100%',
                            minHeight: 40,
                            boxSizing: 'border-box',
                            ...taskChainSectionDividerRowSx,
                          }}
                        >
                          <Typography sx={taskChainFieldLabelTypographySx}>{textLabels.status}</Typography>
                          <Box
                            sx={{ flex: 1, minWidth: 0 }}
                            onClick={event => event.stopPropagation()}
                            onKeyDown={event => event.stopPropagation()}
                          >
                            {canEditTaskFields ? (
                              <TaskChainStatusPicker
                                formState={formState}
                                disabled={!taskFieldsEnabled}
                                onBehalfRows={onBehalfRows}
                                onPick={(recipientId, statusId) => {
                                  updateTaskFormState(task.taskId, previous => ({
                                    ...previous,
                                    selectedRecipientId: recipientId,
                                    selectedStatusId: statusId,
                                  }));
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  minHeight: 40,
                                  py: '8px',
                                  px: '12px',
                                }}
                              >
                                <Typography sx={{ fontSize: '14px', color: '#333333' }}>{missingField}</Typography>
                              </Box>
                            )}
                          </Box>
                        </Stack>

                        <Box sx={{ display: { xs: 'none', lg: 'block' } }} />

                        <Stack direction="row" spacing={0.75} justifyContent="center">
                          {replyActionDisabled ? (
                            <Box component="span" sx={{ display: 'inline-flex' }}>
                              <IconButton
                                size="small"
                                disabled
                                aria-label={t('search.globalSearch.tasks.replyTooltip')}
                                sx={chainTaskActionIconButtonSx}
                              >
                                <Reply size={18} strokeWidth={2} color="currentColor" aria-hidden />
                              </IconButton>
                            </Box>
                          ) : (
                            <Tooltip
                              title={t('search.globalSearch.tasks.replyTooltip')}
                              placement="bottom"
                              enterDelay={400}
                              componentsProps={
                                chainTaskActionTooltipComponentsProps as React.ComponentProps<
                                  typeof Tooltip
                                >['componentsProps']
                              }
                            >
                              <Box component="span" sx={{ display: 'inline-flex' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => onReply(task)}
                                  sx={chainTaskActionIconButtonSx}
                                >
                                  <Reply size={18} strokeWidth={2} color="currentColor" aria-hidden />
                                </IconButton>
                              </Box>
                            </Tooltip>
                          )}
                          {loopOutActionDisabled ? (
                            <Box component="span" sx={{ display: 'inline-flex' }}>
                              <IconButton
                                size="small"
                                disabled
                                aria-label={t('search.globalSearch.tasks.loopOutTooltip')}
                                sx={chainTaskActionIconButtonSx}
                              >
                                <PersonRemoveOutlined fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Tooltip
                              title={t('search.globalSearch.tasks.loopOutTooltip')}
                              placement="bottom"
                              enterDelay={400}
                              componentsProps={
                                chainTaskActionTooltipComponentsProps as React.ComponentProps<
                                  typeof Tooltip
                                >['componentsProps']
                              }
                            >
                              <Box component="span" sx={{ display: 'inline-flex' }}>
                                <IconButton
                                  size="small"
                                  aria-label={t('search.globalSearch.tasks.loopOutTooltip')}
                                  onClick={event => {
                                    event.stopPropagation();
                                    onLoopOut(task);
                                  }}
                                  sx={chainTaskActionIconButtonSx}
                                >
                                  <PersonRemoveOutlined fontSize="small" />
                                </IconButton>
                              </Box>
                            </Tooltip>
                          )}
                          {deleteTaskActionDisabled ? (
                            <Box component="span" sx={{ display: 'inline-flex' }}>
                              <IconButton
                                size="small"
                                disabled
                                aria-label={t('search.globalSearch.tasks.deleteTaskTooltip')}
                                sx={chainTaskActionIconButtonSx}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Tooltip
                              title={t('search.globalSearch.tasks.deleteTaskTooltip')}
                              placement="bottom"
                              enterDelay={400}
                              componentsProps={
                                chainTaskActionTooltipComponentsProps as React.ComponentProps<
                                  typeof Tooltip
                                >['componentsProps']
                              }
                            >
                              <Box component="span" sx={{ display: 'inline-flex' }}>
                                <IconButton
                                  size="small"
                                  aria-label={t('search.globalSearch.tasks.deleteTaskTooltip')}
                                  onClick={event => {
                                    event.stopPropagation();
                                    setTaskPendingPermanentDelete(task);
                                  }}
                                  sx={chainTaskActionIconButtonSx}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Tooltip>
                          )}
                        </Stack>

                        <TextField
                          fullWidth
                          select
                          size="small"
                          label={textLabels.priority}
                          value={formState.priorityId || 2}
                          disabled={!taskFieldsEnabled}
                          onChange={event =>
                            updateTaskFormState(task.taskId, previous => ({
                              ...previous,
                              priorityId: Number(event.target.value),
                            }))
                          }
                          InputLabelProps={{ shrink: true }}
                          sx={embeddedHeaderLabelSx}
                        >
                          {priorityOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          fullWidth
                          size="small"
                          type={formState.followupDateEditable ? 'date' : 'text'}
                          label={textLabels.followUpDate}
                          value={followUpDateValue}
                          disabled={!followUpDateEnabled}
                          onChange={event =>
                            updateTaskFormState(task.taskId, previous => ({
                              ...previous,
                              followupDate: event.target.value,
                            }))
                          }
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: '#666666' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            ...followUpDateFieldSx,
                            ...embeddedHeaderLabelSx,
                            ...(isFollowUpPast && {
                              '& .MuiOutlinedInput-input': { color: '#D32F2F' },
                            }),
                          }}
                        />
                      </Box>

                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', md: 'flex-end' }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={formState.subject || ''}
                            disabled
                            InputProps={{ disableUnderline: true, readOnly: true }}
                            sx={subjectHeadlineFieldSx}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 3,
                            alignItems: 'center',
                            pb: { xs: 0, md: 0.75 },
                          }}
                        >
                          <ToggleOption
                            checked={!!formState.flag}
                            label="Flag"
                            disabled={!taskFieldsEnabled}
                            onChange={checked => {
                              updateTaskFormState(task.taskId, previous => ({ ...previous, flag: checked }));
                            }}
                          />
                          <ToggleOption
                            checked={!!formState.optionApproval}
                            label="Option Approval"
                            disabled={!taskFieldsEnabled || !!task.isFollowup}
                            onChange={checked => {
                              updateTaskFormState(task.taskId, previous => ({ ...previous, optionApproval: checked }));
                            }}
                          />
                        </Box>
                      </Stack>

                      <Stack spacing={1.1}>
                        <Stack direction="row" alignItems="flex-start" sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, minWidth: 0 }}>
                            <Typography sx={taskChainFieldLabelTypographySx}>{textLabels.from}</Typography>
                            <Typography sx={{ fontSize: '14px', color: '#333333' }}>
                              {senderName || '-'}
                              {senderTimestamp ? ` ${senderTimestamp}` : ''}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={event => onInfoIconOpen(event, task.taskId)}
                            sx={{ ml: 'auto', mt: '2px', p: 0.25 }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: '#7A7A7A', flexShrink: 0 }} />
                          </IconButton>
                        </Stack>

                        <Stack direction="row" spacing={1.25} alignItems="flex-start">
                          <Typography sx={{ ...taskChainFieldLabelTypographySx, pt: '4px' }}>{textLabels.to}</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {getToRecipients(task.recipients).map((recipient: ITaskChainRecipient) => (
                              <RecipientChip key={recipient.recipientId} recipient={recipient} />
                            ))}
                          </Box>
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={1.25}
                          alignItems="center"
                          sx={{
                            width: '100%',
                            ...taskChainSectionDividerRowSx,
                          }}
                        >
                          <Typography sx={taskChainFieldLabelTypographySx}>{textLabels.cc}</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {ccRecipients.map((recipient: ITaskChainRecipient) => (
                              <RecipientChip key={`cc-${recipient.recipientId}`} recipient={recipient} />
                            ))}
                            {!ccRecipients.length && (
                              <Typography sx={{ fontSize: '14px', color: '#777777' }}>-</Typography>
                            )}
                          </Box>
                        </Stack>
                      </Stack>

                      <Box sx={{ width: '100%' }}>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            lineHeight: '30px',
                            color: '#333333',
                            whiteSpace: 'pre-line',
                            ...(descriptionExpanded
                              ? {}
                              : {
                                  display: '-webkit-box',
                                  WebkitLineClamp: DESCRIPTION_PREVIEW_LINES,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }),
                          }}
                        >
                          {detailsText || '-'}
                        </Typography>
                        {detailsHasOverflow && (
                          <Link
                            component="button"
                            underline="none"
                            onClick={() =>
                              setDescriptionExpandedByTask(previous => ({
                                ...previous,
                                [task.taskId]: !previous[task.taskId],
                              }))
                            }
                            sx={{ fontSize: '14px', fontWeight: 700, color: '#006FB9', mt: 0.5 }}
                          >
                            {descriptionExpanded ? textLabels.viewLess : textLabels.viewMore}
                          </Link>
                        )}
                      </Box>

                      {!!activeTaskAttachments.length && (
                        <Box sx={{ mt: 0.5, pt: 1.5, borderTop: '1px solid #E8E8E8' }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#444444', mb: 1 }}>
                            {`${textLabels.attachments}:`}
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: '1fr',
                                md: 'repeat(2, minmax(0, 1fr))',
                              },
                              gap: 1,
                            }}
                          >
                            {activeTaskAttachments.map(attachment => (
                              <AttachmentTile
                                key={attachment.taskAttachmentId}
                                attachment={attachment}
                                showDeleteButton={
                                  isTaskSender ||
                                  isCurrentUserCoveringSender(task) ||
                                  isUserCoveringTaskSender(task, activeCoverage)
                                }
                                deleteDisabled={!ENABLE_ATTACHMENT_DELETE || !taskCardFocusEnabled}
                                onDelete={() => setDeleteTarget(attachment)}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {!!deletedTaskAttachments.length && (
                        <Box sx={{ mt: 0.5, pt: 1.5, borderTop: '1px solid #E8E8E8' }}>
                          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#444444', mb: 1 }}>
                            {textLabels.deletedAttachments}
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: '1fr',
                                md: 'repeat(2, minmax(0, 1fr))',
                              },
                              gap: 1,
                            }}
                          >
                            {deletedTaskAttachments.map(item => (
                              <AttachmentTile key={`deleted-${item.taskAttachmentId}`} attachment={item} />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Grid>
        </Grid>
        )}
      </GenericDialogWithNavigation>

      <Popover
        open={!!infoAnchorEl}
        anchorEl={infoAnchorEl}
        onClose={onInfoIconClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            border: '1px solid rgba(224, 224, 224, 1)',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
            maxWidth: 360,
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 280 }}>
          <Stack spacing={1.5}>
            {[...focusedTaskRecipients]
              .sort((a, b) => a.firstName.localeCompare(b.firstName))
              .map(recipient => {
                const statusName = getStatusLabel(recipient.statusId);
                const statusUpdateDateTime = formatDateTime(recipient.statusUpdateDate);
                return (
                  <Box key={`status-history-${recipient.recipientId}`}>
                    <Box sx={{ display: 'inline-flex', maxWidth: '100%' }}>
                      <RecipientChip recipient={recipient} disableTooltip />
                    </Box>
                    <Typography
                      component="div"
                      sx={{ fontSize: '12px', lineHeight: '18px', mt: 0.75, color: '#666666' }}
                    >
                      <Box
                        component="span"
                        sx={{ fontWeight: 600, color: '#555555', fontSize: '13px' }}
                      >
                        {statusName}
                      </Box>
                      {statusUpdateDateTime ? (
                        <Box component="span" sx={{ fontWeight: 400, color: '#666666' }}>
                          {` - ${statusUpdateDateTime}`}
                        </Box>
                      ) : null}
                    </Typography>
                  </Box>
                );
              })}
            {!focusedTaskRecipients.length && (
              <Typography sx={{ fontSize: '12px', color: '#777777' }}>{textLabels.noRecipientStatusData}</Typography>
            )}
          </Stack>
        </Box>
      </Popover>

      <Cancel
        openDialog={showDiscard}
        handleCancelAction={() => setShowDiscard(false)}
        handleConfirmAction={onConfirmDiscard}
      />

      <GenericDialogWithNavigation
        open={!!taskPendingPermanentDelete}
        maxWidth="xs"
        onClose={() => setTaskPendingPermanentDelete(null)}
        dialogTitleProps={{ text: t('search.globalSearch.tasks.deleteTaskTitle') }}
        dialogActions={[
          { text: textLabels.cancel, color: 'tertiary', onClick: () => setTaskPendingPermanentDelete(null) },
          { text: textLabels.confirm, variant: 'contained', onClick: onConfirmPermanentDeleteTask },
        ]}
      >
        {t('search.globalSearch.tasks.deleteTaskMessage')}
      </GenericDialogWithNavigation>

      <GenericDialogWithNavigation
        open={!!deleteTarget}
        maxWidth="xs"
        onClose={() => setDeleteTarget(null)}
        dialogTitleProps={{ text: textLabels.deleteAttachmentTitle }}
        dialogActions={[
          { text: textLabels.cancel, color: 'tertiary', onClick: () => setDeleteTarget(null) },
          { text: textLabels.confirm, variant: 'contained', onClick: onDeleteAttachment },
        ]}
      >
        {textLabels.deleteAttachmentMessage}
      </GenericDialogWithNavigation>

      {!!replyParentTaskDetails && (
        <ReplyToTaskModalNew
          open={!!replyTargetTask}
          parentTaskDetails={replyParentTaskDetails}
          sourceTask={replyTargetTask ?? undefined}
          onCreated={onReplyCreated}
          onClose={onReplyModalClose}
        />
      )}
    </>
  );
};
